import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ArrowRight, Search, PenTool, Rocket, Settings } from 'lucide-react';

const services = [
  {
    icon: Search,
    title: 'Opportunity Assessment',
    description: 'We audit your operations and pinpoint where AI delivers the highest ROI.',
    bestFor: 'Owners who want clarity before committing.',
    outcome: 'A prioritized roadmap with ROI projections.'
  },
  {
    icon: PenTool,
    title: 'Design & Specification',
    description: 'Every flow, integration, and edge case — fully specced before code is written.',
    bestFor: 'Teams ready to solve a specific problem.',
    outcome: 'Complete solution design with timeline.'
  },
  {
    icon: Rocket,
    title: 'Build & Deployment',
    description: 'We build, test, and ship a production AI system integrated with your stack.',
    bestFor: 'Businesses ready to implement now.',
    outcome: 'A live, working system in 30 days.'
  },
  {
    icon: Settings,
    title: 'Ongoing Optimization',
    description: 'Monthly tuning, monitoring, and reporting as your business evolves.',
    bestFor: 'Teams that want continuous improvement.',
    outcome: 'A system that gets sharper every month.'
  }
];

const ServicesSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);

  return (
    <section id="services" className="py-20 bg-[#0A0A0A] relative overflow-hidden">
      {/* Decorative orange orbs */}
      <div className="orange-orb orange-orb-1 top-1/4 -left-32 w-96 h-96" />
      <div className="orange-orb orange-orb-2 bottom-1/4 -right-32 w-[28rem] h-[28rem]" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={headerVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF6B00] mb-5">
            What We Build
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-3">
            Custom AI, built end-to-end.
          </h2>
          <p className="text-[#A1A1AA] text-sm md:text-base max-w-xl mx-auto">
            From discovery to launch — production AI systems tailored to your business.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                animate={headerVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
                whileHover={{ y: -4 }}
                className="group bg-[#0A0A0A] rounded-2xl border border-white/[0.08] hover:border-white/10 p-6 transition-all duration-300 card-glow-hover"
                data-testid={`service-card-${index}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/15 border border-[#FF6B00]/20 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#FF6B00]" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{service.title}</h3>
                </div>
                <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">{service.description}</p>
                <div className="flex gap-4 text-xs">
                  <div className="flex-1 bg-white/[0.03] rounded-lg px-3 py-2">
                    <span className="font-semibold text-white">Best for: </span>
                    <span className="text-[#A1A1AA]">{service.bestFor}</span>
                  </div>
                  <div className="flex-1 bg-white/[0.03] rounded-lg px-3 py-2">
                    <span className="font-semibold text-white">Outcome: </span>
                    <span className="text-[#A1A1AA]">{service.outcome}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={headerVisible ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center mt-10"
        >
          <motion.a
            href="https://calendly.com/agenticatoms/30min"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-2 px-7 py-3 bg-white text-black rounded-full text-sm font-medium shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
            data-testid="services-cta"
          >
            Book a Free 30-Min AI Strategy Call
            <ArrowRight className="w-4 h-4" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
